import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCheck, Send } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { useHolidays } from '@/hooks/useHolidays';
import type { AttendanceSettingsValue } from '@/hooks/useAttendanceSettings';
import { Button } from '@/components/Button';
import { DateLabel } from '@/components/DateLabel';
import { MonthPicker } from '@/components/MonthPicker';
import { Modal } from '@/components/Modal';
import { StatusBadge } from '@/components/WorkRecordModal';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { computesCrossesMidnight } from '@/components/TimeRangeInput';
import { MonthlyEntryDaySheet, makeEmptyShift, type DayEntry, type ShiftEntry, type ReasonCode } from '@/components/MonthlyEntryDaySheet';
import { PlannedTimeConfirmDialog } from '@/components/PlannedTimeConfirmDialog';
import type { BreakRow } from '@/components/BreaksEditor';
import { buildLocalIso, isoToLocalHm, isoToLocalDateTime } from '@/lib/shift-format';
import { addDays } from '@/lib/calendar-day';
import { calcLaborMinutes, resolveAutoDeductBreakMinutes } from '@/lib/labor-hours';
import { flattenNodes } from '@/lib/node-tree';
import type { NodeTreeItem, NodesResponse } from '@/stores/scope';
import type { StoreOption } from '@/components/StoreSinglePicker';

/**
 * `/shift/monthly-entry` (v0.4後半 月次実績一括入力、第2フェーズ I-4b)。
 * 設計: docs/design/v04-monthly-attendance-bulk-entry-phase2-detail.md §6-2
 *
 * フェーズ1 (§5-1) のテーブル/カード2段切替から、日別の「1日分のシート」を開いて編集する方式に
 * 刷新する。器は3段階で切り替える: 767px以下=下から開くシート/768〜1279px=右から重ねるModal/
 * 1280px以上=一覧+右パネル。中身 (MonthlyEntryDaySheet) は3段とも共通。
 *
 * ⚠️ 今回のセッションで意図的に簡略化した点 (残課題、次回セッションで要否を相談):
 * 1. `monthly_entry_node_picker_mode` (店舗ピッカーの「一覧/検索」切替設定、D-993・§3-6) は
 *    catalog行のみ先行投入済みで未実装。本画面は常に「検索欄+一覧」の1種類のUIで統一する。
 * 2. 端末へのローカル一時保存 (§6-2「サーバー保存とは別にlocalStorageへも保存」) は実装済み
 *    (2026-09-28、大規模実機テスト中に発見したデータ消失を機に対応、`draftStorageKey`)。
 *    店舗・対象者・対象月の組合せごとに保存し、ロック済み(提出・承認済み)の日は上書きしない。
 * 3. 最低休憩不足の警告 (§6-2) は未実装 (ミラー実装の罠 §8-134 を避けるため専用の解決API無しでは
 *    フロントで再計算しない判断)。
 * 4. 320px幅での2段折り返し・要素間引きの厳密な仕様 (§6-2 4〜5巡目 design-expert指摘) は
 *    実機確認前のため未調整。design-expert レビュー後に反映する。
 */

type ReasonCodesResponse = { reason_codes: ReasonCode[] };
type WorkRecord = {
  work_record_id: string; employee_id: string | null; external_person_id: string | null;
  work_date: string; start_at: string; end_at: string; break_minutes: number;
  actual_labor_minutes: number; record_kind: 'worked' | 'absence';
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  reason_code_id: string | null; entered_by_user_id: string; row_version: number;
  reason_code_label: string | null; reason_code_category: string | null;
  work_node_id: string;
  submitted_at: string | null; approved_at: string | null;
  planned_shift_pattern_id: string | null; deviation_reason_code_id: string | null; planned_time_confirmed: 0 | 1;
  breaks: Array<{ start_at: string; end_at: string }>;
};
type RosterEntry = { employee_id: string; name: string };
type SummaryPreview = {
  determinable: boolean; reason?: string;
  summary_profile?: 'standard' | 'hourly_night_split' | 'days_planned_actual';
  overtime_minutes?: number | null; night_minutes?: number; night_shift_days?: number; holiday_minutes?: number | null;
  total_labor_minutes?: number; worked_days?: number; scheduled_work_days_contracted?: number | null;
  reason_code_day_counts?: ReasonCodeDayCount[];
};
type ReasonCodeDayCount = { kind: 'worked' | 'reason_code' | 'unset_absence'; reason_code_id: string | null; label: string | null; days: number };
const formatHours = (minutes: number | null | undefined): string => `${((minutes ?? 0) / 60).toFixed(1)}h`;
type ResolvedAttendance = { value: AttendanceSettingsValue | null };

const SELF_OPTION = '__self__';
const RECENT_STORES_KEY = 'misespark:monthly-entry:recent-stores';

const SUMMARY_UNDETERMINABLE_LABELS: Record<string, string> = {
  no_attendance_settings: '勤怠設定が未登録のため集計できません',
  variable_working_hours: 'この労働時間制では残業時間を自動算出できません',
};
const SKIP_REASON_LABELS: Record<string, string> = {
  FUTURE_DATE: '未来日', ALREADY_SUBMITTED: '提出済み', ALREADY_APPROVED: '承認済み',
  ALREADY_LOCKED: '日報ロック中', FORBIDDEN: '権限外', MULTIPLE_EXISTING_RECORDS: '複数レコードあり',
  LEAVE_BALANCE_INSUFFICIENT: '休暇残数不足', LEAVE_MANAGEMENT_DISABLED: '休暇管理機能が無効',
  LEAVE_CONSUMPTION_REQUIRES_EMPLOYEE: '外部人材は休暇消化不可', LEAVE_CONSUMPTION_FAILED: '休暇消化に失敗',
  // §13-23/§13-26: 同一社員・同一日への同時確定が競合した場合 (低頻度)。再入力すれば解消する。
  LEAVE_CONSUMPTION_RACE_CONFLICT: '他の操作と競合(再度お試しください)',
  INCOMPLETE: '未入力・入力途中', AMBIGUOUS_MULTIPLE_RECORDS: '既存記録が複数あり判定不能',
  WORK_RECORD_NOT_FOUND: '対象の記録が見つかりません', CONFLICT: '他の変更と競合',
};
const describeSkipped = (skipped: Array<{ work_date?: string; reason?: string }>): string => {
  const byReason = new Map<string, string[]>();
  for (const s of skipped) {
    const label = SKIP_REASON_LABELS[s.reason ?? ''] ?? s.reason ?? '不明';
    byReason.set(label, [...(byReason.get(label) ?? []), s.work_date ?? '?']);
  }
  return [...byReason.entries()].map(([label, dates]) => `${label}: ${dates.join('/')}`).join('、');
};
const today = () => new Date().toISOString().slice(0, 10);

const enumerateMonthDates = (month: string): string[] => {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return [];
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return Array.from({ length: lastDay }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
};

const EMPTY_DAY: DayEntry = { reasonCodeId: null, shifts: [] };

const buildEndAtIso = (workDate: string, time: { startTime: string | null; endTime: string | null }): string => {
  const endDate = computesCrossesMidnight(time) ? addDays(workDate, 1) : workDate;
  return buildLocalIso(endDate, time.endTime ?? '00:00');
};

const loadRecentStoreIds = (): string[] => {
  try {
    const raw = localStorage.getItem(RECENT_STORES_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch { return []; }
};
const persistRecentStoreIds = (ids: string[]) => {
  try { localStorage.setItem(RECENT_STORES_KEY, JSON.stringify(ids)); } catch { /* Private Mode等は諦める */ }
};

// §6-2「サーバー保存とは別にlocalStorageへも保存」(設計時点で未実装、リロードで下書き前の入力が
// 消える問題)。店舗・対象者・対象月ごとに分離し、他の組合せの下書きを巻き込まない。
const DRAFT_KEY_PREFIX = 'misespark:monthly-entry:draft:';
const draftStorageKey = (nodeId: string, employeeId: string | null, targetIsSelf: boolean, targetMonth: string): string =>
  `${DRAFT_KEY_PREFIX}${nodeId}:${targetIsSelf ? 'self' : (employeeId ?? '')}:${targetMonth}`;
const loadDraftEntries = (key: string): Record<string, DayEntry> | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed as Record<string, DayEntry> : null;
  } catch { return null; }
};
const persistDraftEntries = (key: string, entries: Record<string, DayEntry>) => {
  try { localStorage.setItem(key, JSON.stringify(entries)); } catch { /* Private Mode等は諦める */ }
};
const clearDraftEntries = (key: string) => {
  try { localStorage.removeItem(key); } catch { /* noop */ }
};

/** 767px以下=sheet / 768〜1279px=tablet(右Modal) / 1280px以上=desktop(右パネル常設) */
const useLayoutTier = (): 'sheet' | 'tablet' | 'desktop' => {
  const [tier, setTier] = useState<'sheet' | 'tablet' | 'desktop'>(() => {
    if (typeof window === 'undefined') return 'desktop';
    const w = window.innerWidth;
    return w <= 767 ? 'sheet' : w <= 1279 ? 'tablet' : 'desktop';
  });
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setTier(w <= 767 ? 'sheet' : w <= 1279 ? 'tablet' : 'desktop');
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return tier;
};

export function MonthlyAttendanceEntryPage() {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const layoutTier = useLayoutTier();

  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  // 店舗(トップの「対象者を選ぶ文脈」用): 既存画面と同じく自分の所属店舗に絞る (own_store/self 系の
  // 越境防止、2026-09-09の実機報告修正を維持)。D-993 (所属外店舗を選べる) は「1日分のシート」内の
  // 店舗ピッカーの対象であり、この文脈選択とは別。
  const storeNodes = useMemo(() => {
    const leaves = flatNodes.filter(({ node }: { node: NodeTreeItem }) => !node.children || node.children.length === 0);
    if (!user?.stores?.length) return leaves;
    const allowed = new Set(user.stores);
    return leaves.filter(({ node }: { node: NodeTreeItem }) => allowed.has(node.node_id));
  }, [flatNodes, user?.stores]);
  // 「1日分のシート」の店舗ピッカー候補: D-993 によりテナント全体の店舗から選べるため、
  // GET /nodes (スコープ絞り込み無し、routes/nodes.ts の設計どおり) をそのまま使う。
  const allStoreOptions: StoreOption[] = useMemo(
    () => flatNodes.filter(({ node }) => node.node_type_code === 'store' && node.status === 'active')
      .map(({ node }) => ({ nodeId: node.node_id, name: node.name })),
    [flatNodes],
  );
  // dayLabel (一覧の1日行) が提出済み/承認済みの日の店舗名を表示するための逆引き。
  const storeNameById = useMemo(() => new Map(allStoreOptions.map((s) => [s.nodeId, s.name])), [allStoreOptions]);

  const [nodeId, setNodeId] = useState('');
  const activeNodeId = nodeId || storeNodes[0]?.node.node_id || '';
  const homeNodeId = activeNodeId; // §6-2「初期値はいつもの所属店舗」の簡略化 (本画面が扱う唯一の文脈)

  const [recentStoreIds, setRecentStoreIds] = useState<string[]>(() => loadRecentStoreIds());
  const onStoreUsed = (nid: string) => {
    setRecentStoreIds((cur) => {
      const next = [nid, ...cur.filter((id) => id !== nid)].slice(0, 5);
      persistRecentStoreIds(next);
      return next;
    });
  };

  const { data: rosterData, isLoading: rosterLoading } = useQuery<RosterEntry[]>({
    queryKey: ['work-records-roster', activeNodeId],
    queryFn: () => apiFetch<RosterEntry[]>(`/work-records/roster?node_id=${activeNodeId}`),
    enabled: !!activeNodeId,
  });
  const roster = rosterData ?? [];
  // ⚠️ ロースター取得中は `roster.length === 0` が「本当に0人」と「まだ読み込み中」を区別できず、
  // targetIsSelf が読み込み完了後に true→false へ揺れ動く (D-193周辺の下書き保存キーが読み込み中の
  // 一瞬だけ別キーになり、下書きの保存/復元が食い違う不具合の原因)。下書きの読み書きだけはロースター
  // 確定後まで待つ。
  const draftContextReady = !!activeNodeId && !rosterLoading;
  const [personKey, setPersonKey] = useState('');
  useEffect(() => { setPersonKey(''); }, [activeNodeId]);
  const employeeId = roster.length === 0 ? null : (personKey === SELF_OPTION ? null : (personKey || null));
  const targetIsSelf = roster.length === 0 || personKey === SELF_OPTION;
  const canPick = roster.length === 0 || personKey !== '';

  const [targetMonth, setTargetMonth] = useState(() => today().slice(0, 7));
  const currentDraftKey = draftStorageKey(activeNodeId, employeeId, targetIsSelf, targetMonth);

  const { data: reasonCodesData } = useQuery<ReasonCodesResponse>({
    queryKey: ['attendance-reason-codes', 'monthly-entry'],
    queryFn: () => apiFetch<ReasonCodesResponse>('/masters/attendance-reason-codes'),
  });
  const allCodes = reasonCodesData?.reason_codes ?? [];
  const reasonCodes = allCodes.filter((r) => r.category === 'absence' || r.category === 'holiday_work').sort((a, b) => a.sort_order - b.sort_order);
  const deviationReasonCodes = allCodes.filter((r) => r.category === 'late' || r.category === 'early_leave').sort((a, b) => a.sort_order - b.sort_order);
  const allReasonCodesById = useMemo(() => new Map(allCodes.map((r) => [r.reason_code_id, r])), [reasonCodesData]);
  const isWorkedKind = (reasonCodeId: string | null) => reasonCodeId === null || allReasonCodesById.get(reasonCodeId)?.category === 'holiday_work';

  const dates = useMemo(() => enumerateMonthDates(targetMonth), [targetMonth]);
  const rangeFrom = dates[0]; const rangeTo = dates[dates.length - 1];
  const holidays = useHolidays(rangeFrom ?? '', rangeTo ?? '', activeNodeId);

  // user指摘 (2026-09-28): 「未入力日の扱い」(monthly_entry_blank_day_mode) が treat_as_off の
  // 雇用区分 (アルバイト等) は、未入力日があっても「まとめて提出」を妨げない。§0-14参照。
  const { data: entryPolicyData } = useQuery<{ monthly_entry_blank_day_mode: 'required' | 'treat_as_off' }>({
    queryKey: ['work-records-monthly-entry-policy', activeNodeId, employeeId, targetIsSelf],
    queryFn: () => apiFetch<{ monthly_entry_blank_day_mode: 'required' | 'treat_as_off' }>(
      `/work-records/monthly-entry-policy?work_node_id=${activeNodeId}${employeeId ? `&employee_id=${employeeId}` : ''}`,
    ),
    enabled: !!activeNodeId && canPick,
  });
  const blankDayMode = entryPolicyData?.monthly_entry_blank_day_mode ?? 'required';
  // 「本当に空の日」= 休み区分の選択も勤務シフトの時刻入力も一切していない日 (§4-1の初期化で
  // makeEmptyShift(homeNodeId) が1件だけ入る状態と一致させる)。休み区分を選んだ日・一部でも
  // 時刻を入れた日は blankDayMode に関わらず通常どおり入力必須のまま扱う。
  const isBlankEntry = (entry: DayEntry | undefined): boolean => (
    !!entry && entry.reasonCodeId === null
    && entry.shifts.every((s) => !s.time.startTime && !s.time.endTime)
  );

  const recordsQueryKey = ['work-records-monthly-entry', activeNodeId, employeeId, targetIsSelf, targetMonth];
  const { data: recordsData, isLoading: recordsLoading } = useQuery<{ work_records: WorkRecord[] }>({
    queryKey: recordsQueryKey,
    queryFn: () => apiFetch<{ work_records: WorkRecord[] }>(
      `/work-records?node_id=${activeNodeId}&from=${rangeFrom}&to=${rangeTo}${employeeId ? `&employee_id=${employeeId}` : ''}`,
    ),
    enabled: !!activeNodeId && !!rangeFrom && canPick,
  });
  const existingByDate = useMemo(() => {
    const map = new Map<string, WorkRecord[]>();
    for (const r of recordsData?.work_records ?? []) map.set(r.work_date, [...(map.get(r.work_date) ?? []), r]);
    return map;
  }, [recordsData]);

  const [entries, setEntries] = useState<Record<string, DayEntry>>({});
  useEffect(() => {
    const next: Record<string, DayEntry> = {};
    for (const date of dates) {
      const existing = existingByDate.get(date) ?? [];
      const editable = existing.filter((r) => r.status === 'draft' || r.status === 'rejected');
      // ⚠️ 未入力日は既定で reasonCodeId=null (=勤務扱い、isWorkedKind) のため、shifts を空のまま
      // 初期化すると MonthlyEntryDaySheet の updateShift (entry.shifts.map で書き換える実装) が
      // 空配列に対して no-op になり、入力しても保存されない不具合になる。必ず1件の空シフトを持たせる。
      if (editable.length === 0 && existing.length === 0) { next[date] = { reasonCodeId: null, shifts: [makeEmptyShift(homeNodeId)] }; continue; }
      if (editable.length === 0) continue; // submitted/approved のみ → シート側が lockedRecords から表示
      const absence = editable.find((r) => r.record_kind === 'absence');
      if (absence) { next[date] = { reasonCodeId: absence.reason_code_id, shifts: [] }; continue; }
      const sorted = [...editable].sort((a, b) => a.start_at.localeCompare(b.start_at));
      next[date] = {
        reasonCodeId: sorted[0]?.reason_code_id ?? null,
        shifts: sorted.map((r): ShiftEntry => ({
          key: r.work_record_id, workRecordId: r.work_record_id, workNodeId: r.work_node_id,
          plannedShiftPatternId: r.planned_shift_pattern_id, plannedTimeConfirmed: r.planned_time_confirmed,
          time: { startTime: isoToLocalHm(r.start_at), endTime: isoToLocalHm(r.end_at) },
          breakMinutes: String(r.break_minutes),
          breaks: r.breaks.map((b): BreakRow => ({ startTime: isoToLocalHm(b.start_at), endTime: isoToLocalHm(b.end_at) })),
          deviationReasonCodeId: r.deviation_reason_code_id,
        })),
      };
    }
    // 未保存のままリロード/画面遷移した入力を復元する (§6-2、上記コメント2)。ロック済み(提出・承認済み)
    // の日は、その間にサーバー側の状態が進んでいるため古い下書きで上書きしない。ロースター読み込み中
    // (draftContextReady=false) は currentDraftKey が確定しないため復元しない (rosterLoading完了後の
    // 再実行で改めて行う)。
    if (draftContextReady) {
      const draft = loadDraftEntries(currentDraftKey);
      if (draft) {
        for (const date of dates) {
          if (isLocked(date)) continue;
          if (draft[date]) next[date] = draft[date];
        }
      }
    }
    setEntries(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNodeId, employeeId, targetIsSelf, targetMonth, recordsData, draftContextReady]);

  const isLocked = (date: string) => (existingByDate.get(date) ?? []).some((r) => r.status === 'submitted' || r.status === 'approved');
  const isFuture = (date: string) => date > today();

  // ペイロード組立に使う「行の勤務店舗」ごとの休憩記録方式・自動控除設定 (§5-1/§5-5と同じ原則、
  // シート内の同種フックとクエリキーを揃えてキャッシュを共有する)。
  const distinctShiftNodeIds = useMemo(() => {
    const ids = new Set<string>();
    for (const e of Object.values(entries)) for (const s of e.shifts) ids.add(s.workNodeId);
    return [...ids];
  }, [entries]);
  const attendanceSettingsResults = useQueries({
    queries: distinctShiftNodeIds.map((nid) => ({
      queryKey: ['attendance-settings-resolved', nid],
      queryFn: () => apiFetch<ResolvedAttendance>(`/settings/attendance?node_id=${encodeURIComponent(nid)}`),
    })),
  });
  const attendanceSettingsByNode = useMemo(() => {
    const map = new Map<string, AttendanceSettingsValue | undefined>();
    distinctShiftNodeIds.forEach((nid, i) => map.set(nid, attendanceSettingsResults[i]?.data?.value ?? undefined));
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distinctShiftNodeIds, JSON.stringify(attendanceSettingsResults.map((r) => r.data))]);

  const resolveShiftBreakMinutesAndLabor = (date: string, shift: ShiftEntry): { breakMinutes: number; laborMinutes: number | null; breaksPayload?: Array<{ start_at: string; end_at: string }> } => {
    const settings = attendanceSettingsByNode.get(shift.workNodeId);
    if (!shift.time.startTime || !shift.time.endTime) return { breakMinutes: 0, laborMinutes: null };
    const startAt = buildLocalIso(date, shift.time.startTime);
    const endAt = buildEndAtIso(date, shift.time);
    if (settings?.breakRecordMode === 'time_range') {
      const validRows = shift.breaks.filter((b) => b.startTime && b.endTime);
      const breaksPayload = validRows.map((b) => ({ start_at: buildLocalIso(date, b.startTime!), end_at: buildLocalIso(date, b.endTime!) }));
      const breakMinutes = validRows.reduce((sum, b) => {
        const s = new Date(buildLocalIso(date, b.startTime!)).getTime(); const e = new Date(buildLocalIso(date, b.endTime!)).getTime();
        return sum + Math.max(0, Math.round((e - s) / 60000));
      }, 0);
      const laborMinutes = calcLaborMinutes(startAt, endAt, breakMinutes, settings.breakMode, settings.breakRules);
      return { breakMinutes, laborMinutes, breaksPayload };
    }
    const durationMinutes = Math.round((new Date(endAt).getTime() - new Date(startAt).getTime()) / 60000);
    const breakMinutes = settings?.breakMode === 'auto_deduct'
      ? (resolveAutoDeductBreakMinutes(durationMinutes, settings.breakRules ?? []) ?? 0)
      : (Number(shift.breakMinutes) || 0);
    const laborMinutes = calcLaborMinutes(startAt, endAt, breakMinutes, settings?.breakMode ?? 'clock', settings?.breakRules ?? []);
    return { breakMinutes, laborMinutes };
  };

  type MonthlyEntryDayPayload = {
    work_record_id: string | null; work_date: string; work_node_id?: string; reason_code_id: string | null;
    start_at: string | null; end_at: string | null; break_minutes?: number;
    breaks?: Array<{ start_at: string; end_at: string }>;
    planned_shift_pattern_id?: string | null; deviation_reason_code_id?: string | null; planned_time_confirmed?: 0 | 1;
  };

  const buildDaysPayload = (entriesSource: Record<string, DayEntry> = entries): MonthlyEntryDayPayload[] => dates
    .filter((d) => !isLocked(d) && !isFuture(d))
    .flatMap((date): MonthlyEntryDayPayload[] => {
      const entry = entriesSource[date] ?? EMPTY_DAY;
      if (blankDayMode === 'treat_as_off' && isBlankEntry(entry)) return [];
      if (!isWorkedKind(entry.reasonCodeId)) {
        // ⚠️ entry.shifts[0] が残っていれば (2勤務→休みへの切替直後、MonthlyEntryDaySheet.setReasonCodeId
        // 参照) その work_record_id を明示し、resolveMonthlyEntryDayMatches の
        // AMBIGUOUS_MULTIPLE_RECORDS スキップを避ける (省略すると既存行が2件のときに一致できない)。
        return [{ work_record_id: entry.shifts[0]?.workRecordId ?? null, work_date: date, reason_code_id: entry.reasonCodeId, start_at: null, end_at: null }];
      }
      if (entry.shifts.length === 0) return [{ work_record_id: null, work_date: date, reason_code_id: null, start_at: null, end_at: null }];
      return entry.shifts.map((s): MonthlyEntryDayPayload => {
        const { breakMinutes, breaksPayload } = resolveShiftBreakMinutesAndLabor(date, s);
        return {
          work_record_id: s.workRecordId, work_date: date, work_node_id: s.workNodeId,
          reason_code_id: entry.reasonCodeId,
          start_at: s.time.startTime ? buildLocalIso(date, s.time.startTime) : null,
          end_at: s.time.endTime ? buildEndAtIso(date, s.time) : null,
          break_minutes: breaksPayload ? undefined : breakMinutes,
          breaks: breaksPayload,
          planned_shift_pattern_id: s.plannedShiftPatternId,
          deviation_reason_code_id: s.deviationReasonCodeId,
          planned_time_confirmed: s.plannedTimeConfirmed,
        };
      });
    });

  const submitMut = useMutation({
    mutationFn: (daysPayload: unknown[]) => apiFetch('/work-records/monthly-bulk-submit', {
      method: 'POST',
      body: JSON.stringify({ work_node_id: activeNodeId, employee_id: targetIsSelf ? null : employeeId, external_person_id: null, target_month: targetMonth, days: daysPayload }),
    }),
    onSuccess: (res: unknown) => {
      const created = (res as { created?: string[] })?.created?.length ?? 0;
      const updated = (res as { updated?: string[] })?.updated?.length ?? 0;
      const skipped = (res as { skipped?: Array<{ work_date?: string; reason?: string }> })?.skipped ?? [];
      toast.success(`${created + updated}日分を提出しました${skipped.length > 0 ? `（${skipped.length}日をスキップ: ${describeSkipped(skipped)}）` : ''}`);
      clearDraftEntries(currentDraftKey);
      qc.invalidateQueries({ queryKey: recordsQueryKey });
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '提出に失敗しました'),
  });

  // ⚠️ design-and-code CRITICAL反映: 2勤務の日で1件 (既存保存済み) を削除、または休みの区分へ切替える
  // と、フロントエンドの state 上は shifts から消えるだけで、対応する既存 work_records 行は
  // 未提出のまま残り続ける「孤立行」になる。次回このシートを開かずに保存 (下書き保存/まとめて提出)
  // すると、その日は payload要素1件・既存行2件で resolveMonthlyEntryDayMatches が
  // AMBIGUOUS_MULTIPLE_RECORDS と判定し、リロード後も繰り返しスキップされる恒久的な行き詰まりになる。
  // ⇒ 既存の monthly-bulk-draft が持つ「未入力(start_at/end_at無し)のまま明示 work_record_id を
  // 送ると、その既存行を削除/無効化してからスキップする」既存仕様 (clearExistingDraftForSkip、
  // work-records.ts の INCOMPLETE 分岐) をそのまま再利用し、削除される勤務の行だけを対象にした
  // 最小の下書き保存リクエストを先に送って確実に孤立行を消してから、ローカル state を更新する。
  const clearOrphanShiftMut = useMutation({
    mutationFn: (params: { date: string; workRecordId: string }) => apiFetch('/work-records/monthly-bulk-draft', {
      method: 'POST',
      body: JSON.stringify({
        work_node_id: activeNodeId, employee_id: targetIsSelf ? null : employeeId, external_person_id: null,
        target_month: targetMonth,
        days: [{ work_record_id: params.workRecordId, work_date: params.date, reason_code_id: null, start_at: null, end_at: null }],
      }),
    }),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '削除した勤務の後片付けに失敗しました。再読込のうえやり直してください'),
    onSuccess: () => qc.invalidateQueries({ queryKey: recordsQueryKey }),
  });
  const clearOrphanShiftIfPersisted = async (date: string, shift: ShiftEntry) => {
    if (!shift.workRecordId) return;
    await clearOrphanShiftMut.mutateAsync({ date, workRecordId: shift.workRecordId });
  };

  const saveDraftMut = useMutation({
    mutationFn: () => apiFetch('/work-records/monthly-bulk-draft', {
      method: 'POST',
      body: JSON.stringify({ work_node_id: activeNodeId, employee_id: targetIsSelf ? null : employeeId, external_person_id: null, target_month: targetMonth, days: buildDaysPayload() }),
    }),
    onSuccess: (res: unknown) => {
      const savedCount = ((res as { created?: string[] })?.created?.length ?? 0) + ((res as { updated?: string[] })?.updated?.length ?? 0);
      const skipped = (res as { skipped?: Array<{ work_date?: string; reason?: string }> })?.skipped ?? [];
      const notableSkipped = skipped.filter((s) => s.reason !== 'INCOMPLETE' && s.reason !== 'FUTURE_DATE');
      const base = savedCount > 0 ? `下書きを保存しました（${savedCount}日分）` : '保存できる入力がありませんでした';
      toast.success(notableSkipped.length > 0 ? `${base}（${notableSkipped.length}日を保存できませんでした: ${describeSkipped(notableSkipped)}）` : base);
      clearDraftEntries(currentDraftKey);
      qc.invalidateQueries({ queryKey: recordsQueryKey });
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '下書き保存に失敗しました'),
  });

  const submittedRecords = (recordsData?.work_records ?? []).filter((r) => r.status === 'submitted');
  // ⚠️ (2026-09-28是正、D-1096) バックエンドの自己承認禁止は「対象社員本人」のみをブロックする
  // 仕様に変更された(entered_by_user_id一致では拒否されなくなった、代理入力者=承認者の運用を
  // 許可するため)。表示中の対象がuser自身か (targetIsSelf) で判定する。
  const hasSelfTargetSubmitted = targetIsSelf && submittedRecords.length > 0;
  const approveBulkMut = useMutation({
    mutationFn: () => apiFetch('/work-records/approve-bulk', { method: 'POST', body: JSON.stringify({ work_record_ids: submittedRecords.map((r) => r.work_record_id) }) }),
    onSuccess: (res: unknown) => {
      const approved = (res as { approved?: string[] })?.approved?.length ?? 0;
      const skipped = (res as { skipped?: unknown[] })?.skipped?.length ?? 0;
      toast.success(`${approved}件を承認しました${skipped > 0 ? `（${skipped}件は承認できませんでした）` : ''}`);
      qc.invalidateQueries({ queryKey: recordsQueryKey });
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '承認に失敗しました'),
  });

  // §6-2「予定どおりの確認」: 提出時に1回だけ、予定パターンを選んで未確認のまま (planned_time_confirmed
  // !== 1) の日を一覧表示する。
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const datesNeedingConfirmation = useMemo(() => dates.filter((d) => {
    if (isLocked(d) || isFuture(d)) return false;
    const entry = entries[d];
    return !!entry && entry.shifts.some((s) => s.plannedShiftPatternId && s.plannedTimeConfirmed !== 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [dates, entries, existingByDate]);

  const handleSubmitClick = () => {
    if (datesNeedingConfirmation.length > 0) { setConfirmDialogOpen(true); return; }
    submitMut.mutate(buildDaysPayload());
  };
  const handleConfirmPlannedTime = () => {
    const updated: Record<string, DayEntry> = { ...entries };
    for (const d of datesNeedingConfirmation) {
      const entry = updated[d]; if (!entry) continue;
      updated[d] = { ...entry, shifts: entry.shifts.map((s) => (s.plannedShiftPatternId ? { ...s, plannedTimeConfirmed: 1 } : s)) };
    }
    setEntries(updated);
    setConfirmDialogOpen(false);
    submitMut.mutate(buildDaysPayload(updated));
  };

  // 開いている日 (sheet/tablet=モーダル、desktop=右パネルの選択中日)
  const [openDate, setOpenDate] = useState<string | null>(null);
  const closeSheet = () => setOpenDate(null);
  const copyFromPreviousDay = (date: string) => {
    const idx = dates.indexOf(date);
    const prevDate = dates[idx - 1];
    if (!prevDate) return;
    const prevEntry = entries[prevDate];
    if (!prevEntry) return;
    setEntries((cur) => ({
      ...cur,
      [date]: {
        reasonCodeId: prevEntry.reasonCodeId,
        // ⚠️ §8-228と同型(mutation箇所への配線漏れ、§8-127): makeEmptyShiftはtime/breakMinutesを
        // 空で初期化するため、plannedShiftPatternIdだけコピーすると表示上はパターン名が選択済みに
        // 見えるのに時刻欄が空のまま(実働0扱い)になっていた。前日のtime/breakMinutes/breaksも
        // 引き継ぐことで「前日と同じ」を名実ともに機能させる(2026-09-28実機テストで発見)。
        shifts: prevEntry.shifts.map((s) => ({
          ...makeEmptyShift(s.workNodeId, s.plannedShiftPatternId),
          time: s.time,
          breakMinutes: s.breakMinutes,
          breaks: s.breaks,
        })),
      },
    }));
  };

  const setDayEntry = (date: string, next: DayEntry) => setEntries((cur) => ({ ...cur, [date]: next }));

  const allFilled = dates.filter((d) => !isFuture(d)).every((d) => {
    if (isLocked(d)) return true;
    const entry = entries[d];
    if (!entry) return false;
    if (blankDayMode === 'treat_as_off' && isBlankEntry(entry)) return true;
    if (!isWorkedKind(entry.reasonCodeId)) return true;
    return entry.shifts.length > 0 && entry.shifts.every((s) => s.time.startTime && s.time.endTime);
  });

  // 旧画面と同じ理由 (§8-134 対策): キー入力のたびに毎回プレビューAPIを叩かないよう600msデバウンスする。
  const [debouncedEntries, setDebouncedEntries] = useState(entries);
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedEntries(entries), 600);
    return () => clearTimeout(handle);
  }, [entries]);
  // ⚠️ 下書きのlocalStorage保存は `entries` と `currentDraftKey` を必ず同じスナップショットの組で
  // 書き込む。`debouncedEntries`(600ms遅延)と、その場で再計算される `currentDraftKey` を別々に
  // 参照すると、店舗/対象者/対象月を素早く切り替えた直後は「古い月のentries」を「新しい月のキー」で
  // 保存してしまい、対象の日付が1件も無い(=画面上その日が復元されない)下書きが書き込まれる
  // (2026-09-28、大規模実機テストで発見)。エントリとキーを1つのstateにまとめて同時にデバウンスする。
  const [debouncedDraft, setDebouncedDraft] = useState({ key: currentDraftKey, entries });
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedDraft({ key: currentDraftKey, entries }), 600);
    return () => clearTimeout(handle);
  }, [entries, currentDraftKey]);
  useEffect(() => {
    if (!draftContextReady) return;
    if (debouncedDraft.key !== currentDraftKey) return; // 移行中の古いスナップショットは書かない
    persistDraftEntries(debouncedDraft.key, debouncedDraft.entries);
  }, [debouncedDraft, currentDraftKey, draftContextReady]);
  const { data: summaryPreview } = useQuery<SummaryPreview>({
    queryKey: ['work-records-monthly-summary-preview', activeNodeId, employeeId, targetIsSelf, targetMonth, debouncedEntries],
    queryFn: () => apiFetch<SummaryPreview>('/work-records/monthly-summary-preview', {
      method: 'POST',
      body: JSON.stringify({ work_node_id: activeNodeId, employee_id: targetIsSelf ? null : employeeId, external_person_id: null, target_month: targetMonth, days: buildDaysPayload(debouncedEntries) }),
    }),
    enabled: !!activeNodeId && !!rangeFrom && canPick,
  });

  const dayLabel = (date: string): { text: string; warn: boolean } => {
    const locked = isLocked(date);
    if (locked) {
      const records = existingByDate.get(date) ?? [];
      const primary = records[0];
      return { text: records.length > 1 ? '2店' : (primary?.reason_code_label ?? '勤務'), warn: false };
    }
    const entry = entries[date];
    if (!entry) return { text: '', warn: false };
    if (blankDayMode === 'treat_as_off' && isBlankEntry(entry)) return { text: '休み(未記録)', warn: false };
    if (!isWorkedKind(entry.reasonCodeId)) return { text: allReasonCodesById.get(entry.reasonCodeId ?? '')?.label ?? '休み', warn: false };
    if (entry.shifts.length === 0) return { text: '未入力', warn: false };
    const filled = entry.shifts.every((s) => s.time.startTime && s.time.endTime);
    return { text: entry.shifts.length === 2 ? '2店' : (entry.shifts[0]?.time.startTime ? `${entry.shifts[0].time.startTime}〜${entry.shifts[0].time.endTime ?? ''}` : '未入力'), warn: !filled };
  };

  const sheetDate = openDate ?? (layoutTier === 'desktop' ? (dates.find((d) => !isFuture(d)) ?? dates[0] ?? null) : null);

  const sheetContent = sheetDate && (
    <MonthlyEntryDaySheet
      key={sheetDate}
      date={sheetDate}
      entry={entries[sheetDate] ?? EMPTY_DAY}
      onChange={(next) => setDayEntry(sheetDate, next)}
      locked={isLocked(sheetDate)}
      future={isFuture(sheetDate)}
      lockedRecords={existingByDate.get(sheetDate) ?? []}
      onNavigate={(delta) => {
        const idx = dates.indexOf(sheetDate);
        const next = dates[idx + delta];
        if (next) setOpenDate(next);
      }}
      onCopyFromPreviousDay={() => copyFromPreviousDay(sheetDate)}
      canCopyFromPreviousDay={dates.indexOf(sheetDate) > 0}
      reasonCodes={reasonCodes}
      deviationReasonCodes={deviationReasonCodes}
      allReasonCodesById={allReasonCodesById}
      storeOptions={allStoreOptions}
      homeNodeId={homeNodeId}
      recentStoreIds={recentStoreIds}
      onStoreUsed={onStoreUsed}
      onRemoveShift={(shift) => clearOrphanShiftIfPersisted(sheetDate, shift)}
      // Modal (bottom-sheet/right) が自前の閉じるボタン付きヘッダーを持つため、sheet/tablet 側では
      // MonthlyEntryDaySheet 自身の閉じるボタン (onClose prop) は渡さない (二重防止)。
    />
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-h1">月次実績一括入力</h1>
        <p className="text-sm text-text-sub mt-1">
          シフト予定を組まず、月次でまとめて勤務実績を入力・提出します。日付をタップして1日ずつ入力できます。
        </p>
      </div>

      <div className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
        <div className="flex flex-wrap gap-3">
          {storeNodes.length > 1 && (
            <div className="w-56">
              <label className="block text-xs text-text-sub mb-1">店舗</label>
              <Select value={activeNodeId || undefined} onValueChange={setNodeId}>
                <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="店舗を選ぶ" /></SelectTrigger>
                <SelectContent>{storeNodes.map(({ node }: { node: NodeTreeItem }) => <SelectItem key={node.node_id} value={node.node_id}>{node.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {roster.length > 0 && (
            <div className="w-48">
              <label className="block text-xs text-text-sub mb-1">対象者</label>
              <Select value={personKey || undefined} onValueChange={setPersonKey}>
                <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="選ぶ" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SELF_OPTION}>自分</SelectItem>
                  {roster.map((r) => <SelectItem key={r.employee_id} value={r.employee_id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="block text-xs text-text-sub mb-1">対象年月</label>
            <MonthPicker value={targetMonth} onChange={setTargetMonth} aria-label="対象年月" className="border border-surface-border rounded px-3" />
          </div>
        </div>

        {!activeNodeId ? (
          <p className="text-sm text-text-sub">閲覧できる店舗がありません。</p>
        ) : !canPick ? (
          <p className="text-sm text-text-sub">対象者を選択してください。</p>
        ) : recordsLoading ? (
          <div className="text-text-sub text-sm">読み込み中…</div>
        ) : (
          <div className={layoutTier === 'desktop' ? 'grid grid-cols-[1fr_420px] gap-4 items-start' : ''}>
            {/* 月の一覧 (1日1行) */}
            <div className="space-y-1">
              {dates.map((date) => {
                const { text, warn } = dayLabel(date);
                const locked = isLocked(date);
                const future = isFuture(date);
                const selected = sheetDate === date;
                const records = locked ? (existingByDate.get(date) ?? []) : [];
                const record = records[0];
                // user要望 (2026-09-28): 提出・承認済みの日は一覧の右側が「勤務」としか出ず、どこの
                // 店舗で何時から何時だったか分からなくなっていた (未提出時は時刻を表示していたのに、
                // 提出後に消えていた)。⚠️ この情報を dayLabel の右側テキスト (幅の限られたflex行、
                // 左のバッジと衝突して重なるレイアウト崩れが実機で発生した) には入れず、専用の行として
                // 追加する (店舗名を含む文言は長くなりやすいため、他の要素と幅を競合させない)。
                const workDetail = (record && !record.reason_code_label && records.length === 1)
                  ? `${storeNameById.get(record.work_node_id) ?? record.work_node_id} ${isoToLocalHm(record.start_at)}〜${isoToLocalHm(record.end_at)}`
                  : null;
                // user要望 (2026-09-28): 承認待ち/承認済みバッジだけでは「いつ」提出・承認されたか
                // 分からない。過去月分を後日まとめて代理入力するケース (今回の大規模実機テスト等) では
                // 提出日時が勤務日と大きくずれるため、時刻の併記が特に有用。
                const statusTimestamps: string[] = [];
                if (record?.status === 'submitted' && record.submitted_at) {
                  statusTimestamps.push(`提出: ${isoToLocalDateTime(record.submitted_at)}`);
                } else if (record?.status === 'approved') {
                  if (record.submitted_at) statusTimestamps.push(`提出: ${isoToLocalDateTime(record.submitted_at)}`);
                  if (record.approved_at) statusTimestamps.push(`承認: ${isoToLocalDateTime(record.approved_at)}`);
                }
                return (
                  <button
                    key={date} type="button" onClick={() => setOpenDate(date)}
                    className={`w-full min-h-[44px] flex flex-col justify-center gap-0.5 px-2 py-1.5 rounded text-left text-sm border ${
                      selected && layoutTier === 'desktop' ? 'border-misespark-navy bg-surface-sunken' : 'border-transparent hover:bg-surface-sunken'
                    } ${locked ? 'bg-surface-sunken' : future ? 'opacity-60' : ''}`}
                  >
                    <span className="flex items-center justify-between gap-2 w-full min-w-0">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="shrink-0"><DateLabel date={date} withWeekday holidays={holidays} /></span>
                        {record && <StatusBadge status={record.status} />}
                      </span>
                      <span className="flex items-center gap-1.5 shrink-0 text-xs text-text-sub">
                        {warn && <AlertTriangle size={12} className="text-alert-text shrink-0" />}
                        <span className="truncate max-w-[9rem]">{text}</span>
                      </span>
                    </span>
                    {workDetail && (
                      <span className="text-[11px] text-text-sub tabular-nums block">{workDetail}</span>
                    )}
                    {statusTimestamps.length > 0 && (
                      <span className="text-[11px] text-text-sub tabular-nums block">{statusTimestamps.join(' / ')}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* 右パネル (desktop のみ常設) */}
            {layoutTier === 'desktop' && (
              <div className="border border-surface-border rounded-lg p-4 sticky top-4">
                {sheetContent ?? <p className="text-sm text-text-sub">日付を選んでください。</p>}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 flex-wrap border-t border-surface-border pt-3">
          <div className="text-sm space-y-1">
            {summaryPreview && summaryPreview.total_labor_minutes != null && (() => {
              const profile = summaryPreview.summary_profile ?? 'standard';
              const night = summaryPreview.night_minutes ?? 0;
              const total = summaryPreview.total_labor_minutes ?? 0;
              const overtime = summaryPreview.overtime_minutes;
              return (
                <div className="text-text-sub tabular-nums text-xs flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                  <span className="whitespace-nowrap">
                    出勤日数 {summaryPreview.worked_days ?? 0}日
                    {profile === 'days_planned_actual' && summaryPreview.scheduled_work_days_contracted != null && (
                      <>（所定 {summaryPreview.scheduled_work_days_contracted}日）</>
                    )}
                  </span>
                  <span className="whitespace-nowrap">
                    総労働時間 <span className="text-sm text-text-main font-semibold">{formatHours(total)}</span>
                    {profile === 'hourly_night_split' && (
                      <>（深夜以外 {formatHours(Math.max(0, total - night))}・深夜 {formatHours(night)}）</>
                    )}
                  </span>
                  {profile !== 'hourly_night_split' && (
                    <span className="whitespace-nowrap">深夜時間 {formatHours(night)}</span>
                  )}
                  {overtime != null && (
                    <span className="whitespace-nowrap">
                      残業時間 <span className={overtime > 0 ? 'text-text-body font-semibold' : undefined}>{formatHours(overtime)}</span>
                    </span>
                  )}
                </div>
              );
            })()}
            {(summaryPreview?.reason_code_day_counts?.length ?? 0) > 0 && (() => {
              // 並び順: 勤務 → 勤怠区分マスタの表示順 → 区分未設定
              const rank = (c: ReasonCodeDayCount): number => (c.kind === 'worked' ? -1
                : c.kind === 'unset_absence' ? Number.MAX_SAFE_INTEGER
                  : (allReasonCodesById.get(c.reason_code_id ?? '')?.sort_order ?? Number.MAX_SAFE_INTEGER - 1));
              const labelOf = (c: ReasonCodeDayCount): string => (c.kind === 'worked' ? '勤務'
                : c.kind === 'unset_absence' ? '区分未設定'
                  : (allReasonCodesById.get(c.reason_code_id ?? '')?.label ?? c.label ?? '区分不明'));
              return (
                <div className="text-text-sub tabular-nums text-xs flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                  <span className="whitespace-nowrap">区分別</span>
                  {[...(summaryPreview?.reason_code_day_counts ?? [])].sort((a, b) => rank(a) - rank(b)).map((c) => (
                    <span key={`${c.kind}:${c.reason_code_id ?? ''}`} className="whitespace-nowrap">
                      {labelOf(c)} <span className="text-text-body font-semibold">{c.days}</span>日
                    </span>
                  ))}
                </div>
              );
            })()}
            {summaryPreview && !summaryPreview.determinable && (
              <span className="flex items-start gap-1 text-alert-text px-1.5 py-0.5 rounded bg-alert-bg shrink-0 text-xs">
                <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                {SUMMARY_UNDETERMINABLE_LABELS[summaryPreview.reason ?? ''] ?? '残業時間は算出できません'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="secondary" disabled={saveDraftMut.isPending || submitMut.isPending} loading={saveDraftMut.isPending} onClick={() => saveDraftMut.mutate()}>下書き保存</Button>
            <Button variant="primary" leftIcon={<Send size={14} />} disabled={!allFilled || submitMut.isPending || saveDraftMut.isPending} loading={submitMut.isPending} onClick={handleSubmitClick}>まとめて提出</Button>
          </div>
        </div>

        {submittedRecords.length > 0 && (
          <div className="border-t border-surface-border pt-3 space-y-2">
            {hasSelfTargetSubmitted && <p className="text-xs text-alert-text px-2 py-1.5 rounded bg-alert-bg">自分自身の実績は自分では承認できません。別の承認者に依頼してください。</p>}
            <Button variant="secondary" size="sm" leftIcon={<CheckCheck size={13} />} loading={approveBulkMut.isPending} onClick={() => approveBulkMut.mutate()}>承認待ち{submittedRecords.length}件をまとめて承認</Button>
          </div>
        )}
      </div>

      {/* sheet (mobile、下からのシート) / tablet (右からのModal)。design-expert Critical反映:
          モバイル側もモーダル固有のフォーカストラップ・Escape・スクロールロックを共有するため、
          独自実装ではなく Modal の position='bottom-sheet' バリアントを使う。 */}
      {layoutTier === 'sheet' && (
        <Modal open={!!openDate} onClose={closeSheet} title="1日分の入力" position="bottom-sheet">
          {sheetContent}
        </Modal>
      )}
      {layoutTier === 'tablet' && (
        <Modal open={!!openDate} onClose={closeSheet} title="1日分の入力" position="right">
          {sheetContent}
        </Modal>
      )}

      <PlannedTimeConfirmDialog
        open={confirmDialogOpen}
        dates={datesNeedingConfirmation}
        onClose={() => setConfirmDialogOpen(false)}
        onFixDate={(d) => { setConfirmDialogOpen(false); setOpenDate(d); }}
        onConfirm={handleConfirmPlannedTime}
        submitting={submitMut.isPending}
      />
    </div>
  );
}
